import { z } from "zod";

/**
 * Selbst-Registrierung neuer Kunden (Web und Shopify-App): Pflicht sind nur Firma, Ansprechpartner, E-Mail und die
 * Zustimmung zu AGB/Datenschutz. Die Angaben fürs Angebot sind freiwillig – Logiful bekommt damit eine Grundlage.
 * Beschriftungen (deutsch) für interne Mails und die Kundenverwaltung; das Portal übersetzt selbst.
 */
export const SIGNUP_ORDERS = { lt500: "unter 500", "500_2000": "500–2.000", "2000_10000": "2.000–10.000", gt10000: "über 10.000" } as const;
export const SIGNUP_SKUS = { lt50: "unter 50", "50_500": "50–500", "500_5000": "500–5.000", gt5000: "über 5.000" } as const;
export const SIGNUP_CHANNELS = { shopify: "Shopify", amazon: "Amazon", ebay: "eBay", otto: "OTTO", kaufland: "Kaufland", woocommerce: "WooCommerce", shopware: "Shopware", b2b: "B2B/Handel", other: "Sonstige" } as const;
export const SIGNUP_NEEDS = { best_before: "MHD/Chargen", bulky: "Sperrgut", hazmat: "Gefahrgut", fba: "Amazon-FBA-Belieferung", b2b: "B2B-Lieferungen", returns: "Retourenabwicklung", kitting: "Sets/Konfektionierung" } as const;
export const SIGNUP_START = { asap: "sofort", "1_3m": "in 1–3 Monaten", later: "später" } as const;

const keys = <T extends Record<string, string>>(o: T) => Object.keys(o) as [keyof T & string, ...(keyof T & string)[]];

export const signupOfferSchema = z
  .object({
    ordersPerMonth: z.enum(keys(SIGNUP_ORDERS)).optional(),
    skuCount: z.enum(keys(SIGNUP_SKUS)).optional(),
    channels: z.array(z.enum(keys(SIGNUP_CHANNELS))).max(20).optional(),
    needs: z.array(z.enum(keys(SIGNUP_NEEDS))).max(20).optional(),
    start: z.enum(keys(SIGNUP_START)).optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();
export type SignupOffer = z.infer<typeof signupOfferSchema>;

export const signupSchema = z.object({
  company: z.string().trim().min(2).max(120),
  contactName: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  phone: z.string().trim().max(40).optional(),
  acceptTerms: z.literal(true),
  offer: signupOfferSchema.optional(),
  /** Honeypot: echte Nutzer lassen das (unsichtbare) Feld leer */
  website: z.string().max(200).optional(),
});
export type SignupInput = z.infer<typeof signupSchema>;

/** Angebotsangaben als lesbare Zeilen (interne Mail, Kundenverwaltung) */
export function signupOfferLines(o: SignupOffer | undefined | null): string[] {
  if (!o) return [];
  const lines: string[] = [];
  if (o.ordersPerMonth) lines.push(`Aufträge/Monat: ${SIGNUP_ORDERS[o.ordersPerMonth]}`);
  if (o.skuCount) lines.push(`Artikel: ${SIGNUP_SKUS[o.skuCount]}`);
  if (o.channels?.length) lines.push(`Kanäle: ${o.channels.map((c) => SIGNUP_CHANNELS[c]).join(", ")}`);
  if (o.needs?.length) lines.push(`Besonderheiten: ${o.needs.map((c) => SIGNUP_NEEDS[c]).join(", ")}`);
  if (o.start) lines.push(`Start: ${SIGNUP_START[o.start]}`);
  if (o.note) lines.push(`Notiz: ${o.note}`);
  return lines;
}
